/*
 * Marketing > Forums & Groups tab content.
 *
 * Sizes and presence are RESEARCH ESTIMATES from a manual check (5 Nov 2026), not live counts.
 * Where a size is not known it says "unknown — count manually". No private group invite links
 * are stored here: search for the group by name inside the platform.
 *
 * Task keys (forum-<channelSlug>-<placeSlug>-<actionSlug>) are stored by the roadmap endpoints.
 * Never rename an id once in use, or its tick and comments will no longer match.
 */

export const LAST_CHECKED = "2026-11-05";
export const UNKNOWN_SIZE = "unknown — count manually";

export const PRESENCE = {
  active: { label: "Active", color: "#047857" },
  weak: { label: "Weak / occasional", color: "#b45309" },
  none: { label: "Absent", color: "#b91c1c" },
};

const a = (id, title, detail, effort) => ({ id, title, detail, ...(effort ? { effort } : {}) });

export const CHANNELS = [
  {
    id: "facebook",
    label: "Facebook",
    color: "#1877f2",
    summary: "Biggest parent channel for 11+. Our presence is the EdTech-Ltd company page, not a dedicated 11 Plus Magic page.",
    places: [
      {
        id: "brand-page",
        name: "Our Facebook page (EdTech-Ltd)",
        type: "Brand page",
        sizeNote: UNKNOWN_SIZE,
        leader: { name: "Atom Learning", note: "Dedicated product page with regular parent-facing posts and ads." },
        us: { presence: "weak", note: "Company page named EdTech-Ltd; parents searching '11 Plus Magic' will not find it.", metric: "Followers: " + UNKNOWN_SIZE },
        gap: "No dedicated 11 Plus Magic page, so brand searches and ads lack a clear home.",
        actions: [
          a("dedicated-page", "Create a dedicated 11 Plus Magic Facebook page", "Link from EdTech-Ltd; same logo and handle as Instagram.", "half day"),
          a("cross-link", "Cross-link EdTech-Ltd and the new page", "Pin a post on EdTech-Ltd pointing to 11 Plus Magic."),
          a("weekly-posts", "Post 3 times a week (tips, free test, results)", "Re-use Action Plan organic content."),
          a("reviews", "Turn on Recommendations and ask 5 parents", "Only genuine users."),
        ],
      },
      {
        id: "parent-groups",
        name: "11+ parent groups (regional, e.g. Kent / Bucks / Birmingham)",
        type: "Facebook groups (mostly private)",
        sizeNote: "Ranges from a few hundred to several thousand members each (estimate)",
        leader: { name: "Local tutors / EPP", note: "Tutors who answer questions daily are the trusted voices; groups usually ban direct ads." },
        us: { presence: "none", note: "Not a member of any group we know of.", metric: "Groups joined: 0" },
        gap: "We are absent from where parents ask for advice.",
        actions: [
          a("shortlist", "Shortlist 10 groups by searching '11 plus' in Facebook", "Record name, region, rough size and rules in a comment; no invite links."),
          a("join", "Join 5 groups from a real team member's profile", "Read each group's rules before posting."),
          a("helpful-answers", "Answer 3 parent questions a week with no links", "Build trust first; mention the free test only where rules allow."),
          a("admin-partnership", "Ask one group admin about a free-resource post", "Offer a free mock for members."),
        ],
      },
      {
        id: "own-group",
        name: "Our own 11 Plus Magic parents group",
        type: "Facebook group",
        sizeNote: "Not created yet",
        leader: { name: "Exam Happy", note: "Runs community-style engagement around writing support." },
        us: { presence: "none", note: "No group.", metric: "—" },
        gap: "No owned community for trial users and subscribers.",
        actions: [
          a("create", "Create a private parents group", "Rules, welcome post, weekly theme days."),
          a("invite-users", "Invite trial and paying parents by email", "Link from the Day 0–7 onboarding emails."),
          a("weekly-live", "Run a monthly live Q&A", "Topic per month (mocks, school choice, exam day)."),
        ],
      },
    ],
  },
  {
    id: "instagram",
    label: "Instagram",
    color: "#db2777",
    summary: "Short tips and reels reach parents of Year 4–5 children.",
    places: [
      {
        id: "brand-profile",
        name: "11 Plus Magic Instagram profile",
        type: "Brand profile",
        sizeNote: UNKNOWN_SIZE,
        leader: { name: "Atom Learning", note: "Polished reels and parent testimonials; Exam Happy posts writing tips." },
        us: { presence: "weak", note: "Profile exists or is planned; posting is irregular (check manually).", metric: "Followers: " + UNKNOWN_SIZE },
        gap: "Irregular posting; no reels format.",
        actions: [
          a("bio-link", "Fix bio: what we are + free test link", "Use a UTM link."),
          a("reels", "Post 2 reels a week (one question, 15 seconds)", "Answer revealed at the end."),
          a("highlights", "Add highlights: Free test, Reviews, How it works", "Keep each to 3–5 slides."),
        ],
      },
      {
        id: "hashtags",
        name: "#11plus / #11plusprep hashtag community",
        type: "Hashtags",
        sizeNote: "Tens of thousands of posts (estimate)",
        leader: { name: "Tutors and CGP resellers", note: "Individual tutors post daily." },
        us: { presence: "none", note: "Not using the tags.", metric: "—" },
        gap: "Missing free discovery.",
        actions: [
          a("hashtag-set", "Create a standard hashtag set", "Mix broad (#11plus) and regional (#kent11plus)."),
          a("engage", "Comment helpfully on 5 tutor/parent posts a week", ""),
        ],
      },
    ],
  },
  {
    id: "youtube",
    label: "YouTube",
    color: "#dc2626",
    summary: "Atom and EPP are strong here with explainers and mock walkthroughs.",
    places: [
      {
        id: "brand-channel",
        name: "11 Plus Magic YouTube channel",
        type: "Brand channel",
        sizeNote: UNKNOWN_SIZE,
        leader: { name: "Atom Learning / EPP", note: "Large libraries of explainers and Shorts; EPP walks through papers." },
        us: { presence: "weak", note: "Little or no regular video.", metric: "Subscribers: " + UNKNOWN_SIZE },
        gap: "No search-friendly video library.",
        actions: [
          a("shorts", "Publish 3 Shorts a week (one question each)", "Re-use Instagram reels."),
          a("explainers", "Record 5 longer explainers for top keywords", "Match Keyword Rankings tab topics (VR, NVR, Kent)."),
          a("playlists", "Organise playlists by subject and exam board", ""),
          a("descriptions", "Add site links and timestamps to every description", "UTM-tagged."),
        ],
      },
      {
        id: "tutor-channels",
        name: "Independent 11+ tutor channels",
        type: "Creator channels",
        sizeNote: UNKNOWN_SIZE,
        leader: { name: "Independent tutors", note: "Some tutors have loyal audiences." },
        us: { presence: "none", note: "No collaborations.", metric: "—" },
        gap: "No borrowed audience.",
        actions: [
          a("shortlist", "Shortlist 5 tutor channels", "Note subscriber range and content style."),
          a("collab", "Offer one free-access collaboration", "Tutor reviews the app honestly."),
        ],
      },
    ],
  },
  {
    id: "tiktok",
    label: "TikTok",
    color: "#111827",
    summary: "Younger parents; trial only after core channels work (the Action Plan pauses TikTok ads).",
    places: [
      {
        id: "brand-profile",
        name: "11 Plus Magic TikTok",
        type: "Brand profile",
        sizeNote: UNKNOWN_SIZE,
        leader: { name: "Tutors / education creators", note: "Quick-question videos perform well." },
        us: { presence: "none", note: "Not active (check manually).", metric: "—" },
        gap: "Absent; low priority.",
        actions: [
          a("claim-handle", "Claim the @11plusmagic handle", "Even if not posting yet."),
          a("repost-shorts", "Cross-post the best Shorts once a week", "Measure with UTM links."),
        ],
      },
    ],
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    color: "#16a34a",
    summary: "School-gate and class parent groups share resources by word of mouth.",
    places: [
      {
        id: "parent-chats",
        name: "Class / school parent WhatsApp chats",
        type: "Private chats",
        sizeNote: "Typically 20–200 members each (estimate)",
        leader: { name: "Word of mouth (CGP / Bond books)", note: "Parents recommend what they used." },
        us: { presence: "none", note: "Only via existing parents, untracked.", metric: "—" },
        gap: "No shareable asset for parents to forward.",
        actions: [
          a("share-card", "Create a forwardable free-test message + image", "Short text with a UTM link."),
          a("referral", "Add a 'share with a friend' button in the app", "Track referrals."),
        ],
      },
      {
        id: "channel",
        name: "11 Plus Magic WhatsApp Channel",
        type: "Broadcast channel",
        sizeNote: "Not created yet",
        leader: { name: "None clear", note: "Opportunity: few 11+ brands run channels." },
        us: { presence: "none", note: "No channel.", metric: "—" },
        gap: "First-mover chance.",
        actions: [
          a("create", "Create a WhatsApp Channel for weekly tips", ""),
          a("promote", "Promote it in onboarding emails", ""),
        ],
      },
    ],
  },
  {
    id: "web-forums",
    label: "Web forums",
    color: "#0891b2",
    summary: "The Eleven Plus Exams forum is the biggest dedicated 11+ forum in the UK.",
    places: [
      {
        id: "eleven-plus-exams",
        name: "Eleven Plus Exams forum",
        url: "https://www.elevenplusexams.co.uk/forum/11plus/",
        type: "Web forum",
        sizeNote: "Very large, many thousands of members (estimate)",
        leader: { name: "Eleven Plus Exams", note: "Owns the forum and sells its own papers; experienced parents answer daily." },
        us: { presence: "none", note: "No account posting.", metric: "Posts: 0" },
        gap: "Absent from the most-read 11+ discussion site; self-promotion is restricted.",
        actions: [
          a("account", "Create an account as a named team member", "Disclose affiliation in the signature if allowed."),
          a("read-rules", "Read the advertising rules", "Note them in a comment."),
          a("answer", "Answer 3 questions a week in your expertise", "No links unless asked."),
          a("advertise", "Ask about paid listing / sponsorship", "Compare cost against Google Ads."),
        ],
      },
      {
        id: "mumsnet",
        name: "Mumsnet Education / Secondary forums",
        url: "https://www.mumsnet.com/talk/secondary",
        type: "Web forum",
        sizeNote: "Very large general parenting forum (estimate)",
        leader: { name: "Parents recommending CGP / Bond / Atom", note: "Brands rarely post; recommendations are organic." },
        us: { presence: "none", note: "Not mentioned (check manually).", metric: "—" },
        gap: "Absent from organic recommendations.",
        actions: [
          a("monitor", "Search Mumsnet for '11 plus app' monthly", "Log threads in a comment."),
          a("rules", "Follow Mumsnet's no-advertising rules", "Only genuine parents should mention us."),
        ],
      },
    ],
  },
  {
    id: "reddit",
    label: "Reddit",
    color: "#ea580c",
    summary: "Small for 11+, but threads rank in Google.",
    places: [
      {
        id: "uk-parenting",
        name: "r/UKParenting and r/11plus-style threads",
        url: "https://www.reddit.com/r/UKParenting/",
        type: "Subreddit",
        sizeNote: "Large subreddit; 11+ threads occasional (estimate)",
        leader: { name: "No clear brand leader", note: "Parents mention Atom, CGP and Bond." },
        us: { presence: "none", note: "No account.", metric: "—" },
        gap: "Absent from threads that Google surfaces.",
        actions: [
          a("monitor", "Set a monthly search for '11 plus' on Reddit", ""),
          a("answer", "Answer helpfully with disclosure", "Follow subreddit self-promotion rules."),
        ],
      },
    ],
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    color: "#0a66c2",
    summary: "Light touch: schools, tutors and partners, not parents.",
    places: [
      {
        id: "company-page",
        name: "EdTech Ltd / 11 Plus Magic LinkedIn",
        type: "Company page",
        sizeNote: UNKNOWN_SIZE,
        leader: { name: "Atom Learning", note: "Active company page for hiring and school partnerships." },
        us: { presence: "weak", note: "Check whether a page exists.", metric: "—" },
        gap: "Minor; useful for tutor and school partnerships.",
        actions: [
          a("page", "Create or tidy the company page", "Link 11 Plus Magic as a showcase page."),
          a("monthly-post", "Post one update a month", "Product news, results."),
        ],
      },
    ],
  },
  {
    id: "x",
    label: "X (Twitter)",
    color: "#334155",
    summary: "Optional: low parent usage for 11+.",
    places: [
      {
        id: "brand-account",
        name: "@11plusmagic on X",
        type: "Brand account",
        sizeNote: UNKNOWN_SIZE,
        leader: { name: "CGP", note: "Publisher accounts post product news." },
        us: { presence: "none", note: "Check whether the handle is claimed.", metric: "—" },
        gap: "Low priority.",
        actions: [
          a("claim-handle", "Claim the handle", "Protect the brand."),
          a("auto-share", "Share blog posts automatically", ""),
        ],
      },
    ],
  },
];

export function forumTaskId(channelId, placeId, actionId) {
  return `forum-${channelId}-${placeId}-${actionId}`;
}

export function flattenForumActions(channels = CHANNELS) {
  return channels.flatMap((c) => c.places.flatMap((p) => p.actions.map((act) => ({
    ...act,
    id: forumTaskId(c.id, p.id, act.id),
    actionSlug: act.id,
    channelId: c.id,
    placeId: p.id,
    placeName: p.name,
  }))));
}

export function flattenPlaces(channels = CHANNELS) {
  return channels.flatMap((c) => c.places.map((p) => ({ ...p, channelId: c.id })));
}
